/**
 * Driver API - Driver management and risk profiles
 * @module api/drivers
 */

import { apiRequest } from './core.js';

// =============================================================================
// DRIVER CRUD API
// =============================================================================

/**
 * Driver API - CRUD operations for fleet drivers
 */
export const driverAPI = {
  getAll: () => apiRequest('/driver?_limit=5000'),
  getById: (id) => apiRequest(`/driver/${id}`),
  create: (driver) => apiRequest('/driver', {
    method: 'POST',
    body: JSON.stringify(driver),
  }),
  update: (id, driver) => apiRequest(`/driver/${id}`, {
    method: 'PUT',
    body: JSON.stringify(driver),
  }),
  delete: (id) => apiRequest(`/driver/${id}`, {
    method: 'DELETE',
  }),

  // Availability management
  updateAvailability: (id, availability) => apiRequest(`/drivers/${id}/availability`, {
    method: 'PATCH',
    body: JSON.stringify(availability),
  }),
  getAvailabilitySummary: () => apiRequest('/drivers/availability/summary'),
  getAvailabilityHistory: (id, limit = 20) => apiRequest(`/drivers/${id}/availability/history?_limit=${limit}`),
};

// =============================================================================
// DRIVER SELF-SERVICE API (`/drivers/me/*`)
// =============================================================================

/**
 * Ce que le conducteur lit sur LUI-MÊME. Le périmètre est celui du jeton :
 * aucune de ces routes ne prend d'identifiant de conducteur en paramètre —
 * c'est ce qui rend l'espace conducteur étanche (liste blanche aux deux
 * étages, JGS 2026-09-13).
 *
 * ⚠️ NE PAS confondre avec `/fuel-cards` (routes gestionnaire) : celles-ci
 * listent les cartes de TOUTE l'organisation. Les appeler depuis l'espace
 * conducteur montrerait à un agent les cartes de ses collègues.
 *
 * ⚠️ `/drivers/me/cards` n'est PAS la carte carburant : c'est le mur de cartes
 * de l'app mobile (`app_views`, `nav_group='mobile_driver'`). Mesuré le
 * 2026-09-14 dans `backend/routes/driver_mono_routes.mjs`.
 */
export const driverSelfAPI = {
  /**
   * MA carte carburant (ou mes cartes, un véhicule et un conducteur pouvant en
   * porter chacun une). Réponse attendue : `{ ok, data: [{ id, provider,
   * provider_name, last4, status, card_type, expiry_date, monthly_limit,
   * allowed_fuel_types, pin_required, vehicle_id, vehicle_plate }] }`.
   *
   * ⚠️ Le numéro complet et le code confidentiel ne doivent JAMAIS traverser :
   * le serveur ne rend que les quatre derniers chiffres.
   *
   * Servie par `GET /drivers/me/fuel-cards` (`driver_mono_routes.mjs`, ajoutée le
   * 2026-09-14) : la façade conducteur ne rend que ses propres cartes. Ne jamais
   * lire la route gestionnaire `/fuel-cards`, qui exposerait tout le parc de cartes.
   * Un 404 = pas de carte à montrer, le bloc se tait.
   */
  getMyFuelCards: () => apiRequest('/drivers/me/fuel-cards'),

  /** Ma fiche : `{ ok, data: { driver, vehicle, site } }` — le site porte ses GPS. */
  getMe: () => apiRequest('/drivers/me'),

  /**
   * Préremplissage d'un plein (2026-09-14 : la GARDE DE POOL en cours d'abord, la
   * dotation ensuite — `vehicle_source`). `estimated_odometer` est un REPÈRE, jamais
   * une valeur préremplie : préremplir avait recalé un boîtier de 52 km (JOURNAL_BETA 19/08).
   */
  getFuelPrefill: () => apiRequest('/drivers/me/fuel/prefill'),
  /** Un plein : FormData (`fuel_date`, `liters`, `total_cost`, `odometer`, `is_full_tank`, photos facultatives). */
  createFuelLog: (formData) => apiRequest('/drivers/me/fuel', { method: 'POST', body: formData }),
  listFuelLogs: () => apiRequest('/drivers/me/fuel'),

  /** Mes kilomètres : `{ ok, scale, distance: { total_km, series: [{ bucket, km }] }, vehicles: [...] }`. */
  getActivity: ({ scale = 'week', from, to } = {}) => {
    const params = new URLSearchParams({ scale });
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    return apiRequest(`/drivers/me/activity?${params.toString()}`);
  },

  /** Déclarer un sinistre : FormData (`type`, `event_date`, `event_time`, `location`, `description`, `drivable`, `gps_lat`, `gps_lon`, photos). */
  createClaim: (formData) => apiRequest('/drivers/me/claims', { method: 'POST', body: formData }),

  /** Qui appeler : `{ ok, data: { contacts: [{ kind, name, phone, email }] } }`. */
  getEmergencyContacts: () => apiRequest('/drivers/me/emergency-contacts'),
};

/**
 * Prix du carburant autour d'un point (source prix-carburants.gouv.fr, servie par
 * `/fuel-cards/prices/nearby`). Réponse : `{ ok, data: [{ name, brand, address, city,
 * distance_km, price, lat, lon }] }`, triée par distance.
 */
export const fuelPricesAPI = {
  nearby: ({ lat, lon, radius = 10, fuelType = 'gazole' }) => {
    const params = new URLSearchParams({ lat: String(lat), lon: String(lon), radius: String(radius), fuel_type: fuelType });
    return apiRequest(`/fuel-cards/prices/nearby?${params.toString()}`);
  },
};

// =============================================================================
// DRIVER RISK PROFILE API
// =============================================================================

/**
 * Driver Risk Profile API - Rankings and behavior scores
 */
export const riskProfileAPI = {
  getAll: () => apiRequest('/risk-profiles'),
  getById: (id) => apiRequest(`/risk-profiles/${id}`),
  getByDriver: (driverId) => apiRequest(`/risk-profiles/driver/${driverId}`),
  getRankings: () => apiRequest('/risk-profiles/rankings'),
  getStats: () => apiRequest('/risk-profiles/stats'),
  create: (profile) => apiRequest('/risk-profiles', {
    method: 'POST',
    body: JSON.stringify(profile),
  }),
  update: (id, profile) => apiRequest(`/risk-profiles/${id}`, {
    method: 'PUT',
    body: JSON.stringify(profile),
  }),
  recalculate: (id) => apiRequest(`/risk-profiles/${id}/recalculate`, {
    method: 'POST',
  }),
};
